import axios from "axios";
import * as JSPM from "jsprintmanager";
import toast from "react-hot-toast";

const HOST_URL = process.env.REACT_APP_HOST_URL;

let jspmReadyPromise;

const _ensureJSPMReady = () => {
  console.log("Ensuring JSPM is ready...");
  if (!jspmReadyPromise) {
    jspmReadyPromise = new Promise((resolve, reject) => {
      JSPM.JSPrintManager.license_url = `${HOST_URL}/jspm`;
      JSPM.JSPrintManager.auto_reconnect = true;
      JSPM.JSPrintManager.start();

      const timeout = setTimeout(() => {
        reject(new Error("JSPM WebSocket connection timeout"));
      }, 10000); // 10 second timeout

      const checkReady = () => {
        if (JSPM.JSPrintManager.websocket_status === JSPM.WSStatus.Open) {
          console.log("Socket Is Open Now.");
          clearTimeout(timeout);
          resolve();
        } else {
          setTimeout(checkReady, 100);
        }
      };

      checkReady();
    });
  }
  return jspmReadyPromise;
};

/**
 * Sends an LPN label print job to the default client printer using JSPM.
 *
 * @async
 * @function printLpnLabel
 * @param {Object} labelData - Data for the label to be printed.
 * @param {string} labelData.lpn - The LPN (License Plate Number) to print. **Required**.
 * @param {number} [labelData.quantity] - Number of copies to print. Defaults to `1` if not provided.
 * @returns {Promise<void>} Resolves when the print process completes (success or error).
 *
 * @throws Will show toast notifications for various error states (e.g., print job failure, printer connection issues).
 */
const printLpnLabel = async (labelData) => {
  const user = JSON.parse(localStorage.getItem("user"));
  labelData.userId = user?.userID;

  const response = (await axios.post(`${HOST_URL}/print/lpnLabel`, labelData))
    .data;

  if (!response.success) {
    toast.error(response.message);
    return;
  }

  const fileName = response.fileName;

  try {
    await _ensureJSPMReady();

    const toastId = toast.loading("Sending print job...");
    const cpj = new JSPM.ClientPrintJob();
    cpj.clientPrinter = new JSPM.DefaultPrinter();

    const my_file = new JSPM.PrintFile(
      `${HOST_URL}/print/getLabel/${fileName}`,
      JSPM.FileSourceType.URL,
      "LpnLabel.png",
      labelData.quantity || 1
    );

    cpj.files.push(my_file);

    cpj.onUpdate = (data) => {
      if (data?.result === "ClientPrintJob done") {
        toast.success("Print job completed!", { id: toastId });
      } else if (data?.id === -1) {
        toast.error("Print job canceled!", { id: toastId });
      }
    };

    const result = await cpj.sendToClient();

    if (result) {
      toast.loading("Print job sent...", { id: toastId });
    } else {
      toast.error("Print job filed to send.", { id: toastId });
    }
  } catch (error) {
    console.error("JSPM connection error:", error);
    toast.error("Printer connection failed.");
  }
};

/**
 * Sends a request to generate a listing label image and silently prints it using JSPrintManager.
 *
 * This function:
 * - Posts label data to the server to generate a label image.
 * - Retrieves the filename from the response.
 * - Initializes JSPrintManager to silently print the label image to the client's default printer.
 * - Uses react-hot-toast to notify the user of printing status.
 *
 * @async
 * @function
 * @param {Object} labelData - The data required to generate and print the listing label.
 * @param {string} [labelData.fnsku] - (Optional) FNSKU identifier for the product.
 * @param {string} [labelData.sku] - (Optional) SKU of the listing.
 * @param {string} [labelData.amazonConditionLabel] - (Optional) Amazon condition label (e.g., New, Used).
 * @param {string} [labelData.inspectConditionCode] - (Optional) Inspection code or quality grading.
 * @param {string} [labelData.title] - (Optional) Product title or description.
 * @param {number} labelData.listingAccountId - (Required) Account ID the listing belongs to.
 * @param {number} labelData.listingId - (Required) Unique identifier of the listing.
 * @param {boolean} [labelData.pendingFnsku] - (Optional) Whether the FNSKU is pending assignment.
 * @param {number} labelData.quantity - (Required) Number of label copies to print.
 *
 * @returns {Promise<void>} No return value. Notifies via toast on success or failure.
 *
 * @throws Will show an error toast if the label generation fails or the print job fails.
 */
export const printListingLabel = async (labelData) => {
  const user = JSON.parse(localStorage.getItem("user"));
  labelData.userId = user?.userID;

  // This creates the label image on the server.
  const response = (
    await axios.post(`${HOST_URL}/print/listingLabel`, labelData)
  ).data;

  if (!response.success) {
    toast.error(response.message);
    return;
  }

  const fileName = response.fileName;

  try {
    await _ensureJSPMReady();

    const toastId = toast.loading("Sending print job...");
    const cpj = new JSPM.ClientPrintJob();
    cpj.clientPrinter = new JSPM.DefaultPrinter();

    const my_file = new JSPM.PrintFile(
      `${HOST_URL}/print/getLabel/${fileName}`,
      JSPM.FileSourceType.URL,
      "ListingLabel.png",
      labelData.quantity || 1
    );

    cpj.files.push(my_file);

    cpj.onUpdate = (data) => {
      if (data?.result === "ClientPrintJob done") {
        toast.success("Print job completed!", { id: toastId });
      } else if (data?.id === -1) {
        toast.error("Print job canceled!", { id: toastId });
      }
    };

    const result = await cpj.sendToClient();

    if (result) {
      toast.loading("Print job sent...", { id: toastId });
    } else {
      toast.error("Print job filed to send.", { id: toastId });
    }
  } catch (error) {
    console.error("JSPM connection error:", error);
    toast.error("Printer connection failed.");
  }
};

export const printBinStoreLabels = async (labelData) => {
  const user = JSON.parse(localStorage.getItem("user"));
  labelData.userId = user?.userID;

  // This creates the label images on the server.
  const response = (
    await axios.post(`${HOST_URL}/print/listingLabel/binStore`, labelData)
  ).data;

  if (!response.success) {
    toast.error(response.message);
    return;
  }

  const fileNames = response.fileNames;

  try {
    await _ensureJSPMReady();

    const toastId = toast.loading("Sending print job...");
    const cpj = new JSPM.ClientPrintJob();
    cpj.clientPrinter = new JSPM.DefaultPrinter();

    fileNames.forEach((fileName, index) => {
      const my_file = new JSPM.PrintFile(
        `${HOST_URL}/print/getLabel/${fileName}`,
        JSPM.FileSourceType.URL,
        `ListingLabel-${index + 1}.png`,
        1
      );

      cpj.files.push(my_file);
    });

    cpj.onUpdate = (data) => {
      if (data?.result === "ClientPrintJob done") {
        toast.success("Print job completed!", { id: toastId });
      } else if (data?.id === -1) {
        toast.error("Print job canceled!", { id: toastId });
      }
    };

    const result = await cpj.sendToClient();

    if (result) {
      toast.loading("Print job sent...", { id: toastId });
    } else {
      toast.error("Print job filed to send.", { id: toastId });
    }
  } catch (error) {
    console.error("JSPM connection error:", error);
    toast.error("Printer connection failed.");
  }
};

export const printCartonLabel = async (labelData) => {
  const user = JSON.parse(localStorage.getItem("user"));
  labelData.userId = user?.userID;

  const response = (
    await axios.post(`${HOST_URL}/print/cartonLabel`, labelData)
  ).data;

  if (!response.success) {
    toast.error(response.message);
    return;
  }

  const fileName = response.fileName;

  try {
    await _ensureJSPMReady();

    const toastId = toast.loading("Sending print job...");
    const cpj = new JSPM.ClientPrintJob();
    cpj.clientPrinter = new JSPM.DefaultPrinter();

    const my_file = new JSPM.PrintFile(
      `${HOST_URL}/print/getLabel/${fileName}`,
      JSPM.FileSourceType.URL,
      "CartonLabel.png",
      labelData.quantity || 1
    );

    cpj.files.push(my_file);

    cpj.onUpdate = (data) => {
      if (data?.result === "ClientPrintJob done") {
        toast.success("Print job completed!", { id: toastId });
      } else if (data?.id === -1) {
        toast.error("Print job canceled!", { id: toastId });
      }
    };

    const result = await cpj.sendToClient();

    if (result) {
      toast.loading("Print job sent...", { id: toastId });
    } else {
      toast.error("Print job filed to send.", { id: toastId });
    }
  } catch (error) {
    console.error("JSPM connection error:", error);
    toast.error("Printer connection failed.");
  }
};

export const printPalletLabel = async (labelData) => {
  const user = JSON.parse(localStorage.getItem("user"));
  labelData.userId = user?.userID;

  const response = (
    await axios.post(`${HOST_URL}/print/palletLabel`, labelData)
  ).data;

  if (!response.success) {
    toast.error(response.message);
    return;
  }

  const fileName = response.fileName;

  try {
    await _ensureJSPMReady();

    const toastId = toast.loading("Sending print job...");
    const cpj = new JSPM.ClientPrintJob();
    cpj.clientPrinter = new JSPM.DefaultPrinter();

    const my_file = new JSPM.PrintFile(
      `${HOST_URL}/print/getLabel/${fileName}`,
      JSPM.FileSourceType.URL,
      "PalletLabel.png",
      labelData.quantity || 1
    );

    cpj.files.push(my_file);

    cpj.onUpdate = (data) => {
      if (data?.result === "ClientPrintJob done") {
        toast.success("Print job completed!", { id: toastId });
      } else if (data?.id === -1) {
        toast.error("Print job canceled!", { id: toastId });
      }
    };

    const result = await cpj.sendToClient();

    if (result) {
      toast.loading("Print job sent...", { id: toastId });
    } else {
      toast.error("Print job filed to send.", { id: toastId });
    }
  } catch (error) {
    console.error("JSPM connection error:", error);
    toast.error("Printer connection failed.");
  }
};

export const printFbaShipmentLabel = async (labelUrl) => {
  const user = JSON.parse(localStorage.getItem("user"));
  const userId = user?.userID;

  const response = (
    await axios.post(`${HOST_URL}/print/fbaShipmentLabel`, {
      labelUrl,
      userId,
    })
  ).data;

  let fileName;
  if (response.success) {
    fileName = response.fileName;
  } else {
    toast.error(response.message);
    return;
  }

  const printingToast = toast.loading("Printing label...");

  JSPM.JSPrintManager.license_url = `${HOST_URL}/jspm`;
  JSPM.JSPrintManager.auto_reconnect = true;
  JSPM.JSPrintManager.start();
  JSPM.JSPrintManager.WS.onStatusChanged = async function () {
    if (JSPM.JSPrintManager.websocket_status === JSPM.WSStatus.Open) {
      // Getting the list of printers
      const printerList = await JSPM.JSPrintManager.getPrintersInfo();

      const cpj = new JSPM.ClientPrintJob();
      cpj.clientPrinter = new JSPM.InstalledPrinter("iDPRT SP410");

      // This is for printing a pdf directly.
      // const my_file = new JSPM.PrintFilePDF(
      //   `${HOST_URL}/print/getLabel/${fileName}`,
      //   JSPM.FileSourceType.URL,
      //   "FbaShipmentLabel.pdf",
      //   1
      // );

      const my_file = new JSPM.PrintFile(
        `${HOST_URL}/print/getLabel/${fileName}`,
        JSPM.FileSourceType.URL,
        "FbaShipmentLabel.png",
        1
      );

      cpj.files.push(my_file);

      cpj.jobInfo = {
        states: "",
        printedPages: 0,
        printedBytes: 0,
        totalPages: 0,
        totalBytes: 0,
      };

      cpj.onUpdated = function (data) {
        console.warn(data);
        if (data.id) {
          if (
            data["completed-pages"] &&
            data["completed-pages"] > cpj.jobInfo.printedPages
          ) {
            cpj.jobInfo.printedPages = data["completed-pages"];
          }
          if (
            data["total-pages"] &&
            data["total-pages"] > cpj.jobInfo.totalPages
          ) {
            cpj.jobInfo.totalPages = data["total-pages"];
          }
          if (
            data["total-bytes"] &&
            data["total-bytes"] > cpj.jobInfo.totalBytes
          ) {
            cpj.jobInfo.totalBytes = data["total-bytes"];
          }
          if (
            data["printed-bytes"] &&
            data["printed-bytes"] > cpj.jobInfo.printedBytes
          ) {
            cpj.jobInfo.printedBytes = data["printed-bytes"];
          }
          if (data["state-description"]) {
            cpj.jobInfo.states += "," + data["state-description"];
          }
        }

        if (data.result === "ClientPrintJob done") {
          // SOME SOURCES ABOUT THE MEANING OF SOME STATES
          // RETAINED: https://stackoverflow.com/q/26862882
          // COMPLETE: https://learn.microsoft.com/en-us/dotnet/api/system.printing.printjobstatus

          let success =
            (cpj.jobInfo.totalBytes > 0 &&
              cpj.jobInfo.printedBytes > 0 &&
              cpj.jobInfo.printedBytes >= cpj.jobInfo.totalBytes) ||
            (cpj.jobInfo.totalPages > 0 &&
              cpj.jobInfo.printedPages > 0 &&
              cpj.jobInfo.printedPages >= cpj.jobInfo.totalPages) ||
            cpj.jobInfo.states.includes("PRINTED") ||
            cpj.jobInfo.states.includes("RETAINED") ||
            cpj.jobInfo.states.includes("COMPLETE");

          if (success) {
            console.log(`Print success`);
            toast.dismiss(printingToast);
            toast.success("Finished printing!");
          } else {
            console.log(`Print with error`);
          }
        }
      };

      cpj.onFinished = function (data) {
        console.warn(data);
      };

      cpj.onError = function (data) {
        console.warn(data);
      };

      const result = await cpj.sendToClient();

      if (result) {
        toast.dismiss(printingToast);
        toast.success("Finished printing!");
      }
    }
  };

  return fileName;
};

/* param - shipmentRackList - array of objects,
 ** each object has shipmentRackId (int) and bayName (string)
 */
export const printRackLabels = async (shipmentRackList) => {
  const user = JSON.parse(localStorage.getItem("user"));
  const userId = user?.userID;

  const response = (
    await axios.post(`${HOST_URL}/print/rackLabels`, {
      shipmentRackList,
      userId,
    })
  ).data;

  let fileNameList;
  if (response.success) {
    fileNameList = response.fileNameList;
  } else {
    toast.error(response.message);
    return;
  }

  JSPM.JSPrintManager.license_url = `${HOST_URL}/jspm`;
  JSPM.JSPrintManager.auto_reconnect = true;
  JSPM.JSPrintManager.start();
  JSPM.JSPrintManager.WS.onStatusChanged = async function () {
    if (JSPM.JSPrintManager.websocket_status === JSPM.WSStatus.Open) {
      const cpj = new JSPM.ClientPrintJob();
      cpj.clientPrinter = new JSPM.DefaultPrinter();

      fileNameList.forEach((fileName, index) => {
        const my_file = new JSPM.PrintFile(
          `${HOST_URL}/print/getLabel/${fileName}`,
          JSPM.FileSourceType.URL,
          `RackLabel-${index + 1}.png`,
          1
        );

        cpj.files.push(my_file);
      });

      cpj.onFinished = () => {
        toast.success("Finished printing!");
      };

      cpj.sendToClient();
    }
  };
};

export const generateFbaShipmentLabels = async ({
  shipmentId,
  start,
  quantity,
}) => {
  const user = JSON.parse(localStorage.getItem("user"));
  const userId = user?.userID;

  const response = (
    await axios.post(`${HOST_URL}/print/generateFbaShipmentLabels`, {
      userId,
      shipmentId,
      start,
      quantity,
    })
  ).data;

  let fileNameList;
  if (response.success) {
    fileNameList = response.fileNameList;
    console.log(fileNameList);
  } else {
    toast.error(response.message);
    return;
  }

  const printingToast = toast.loading("Printing label(s)...");

  JSPM.JSPrintManager.license_url = `${HOST_URL}/jspm`;
  JSPM.JSPrintManager.auto_reconnect = true;
  JSPM.JSPrintManager.start();
  JSPM.JSPrintManager.WS.onStatusChanged = async function () {
    if (JSPM.JSPrintManager.websocket_status === JSPM.WSStatus.Open) {
      const cpj = new JSPM.ClientPrintJob();
      cpj.clientPrinter = new JSPM.InstalledPrinter("iDPRT SP410");

      fileNameList.forEach((fileName, index) => {
        const my_file = new JSPM.PrintFile(
          `${HOST_URL}/print/getLabel/${fileName}`,
          JSPM.FileSourceType.URL,
          `FbaShipmentLabel-${index + 1}.png`,
          1
        );

        cpj.files.push(my_file);
      });

      cpj.onFinished = () => {
        toast.dismiss(printingToast);
        toast.success("Finished printing!");
      };

      cpj.sendToClient();
    }
  };
};

export const printBoxContentLabel = async ({ shipmentCartonId }) => {
  const user = JSON.parse(localStorage.getItem("user"));
  const userId = user?.userID;

  const response = (
    await axios.post(`${HOST_URL}/print/boxContentLabel`, {
      userId,
      shipmentCartonId,
    })
  ).data;

  let fileName;
  if (response.success) {
    fileName = response.fileName;
  } else {
    toast.error(response.message);
    return { message: response.message };
  }

  const printingToast = toast.loading("Printing label...");

  JSPM.JSPrintManager.license_url = `${HOST_URL}/jspm`;
  JSPM.JSPrintManager.auto_reconnect = true;
  JSPM.JSPrintManager.start();
  JSPM.JSPrintManager.WS.onStatusChanged = async function () {
    if (JSPM.JSPrintManager.websocket_status === JSPM.WSStatus.Open) {
      const cpj = new JSPM.ClientPrintJob();
      cpj.clientPrinter = new JSPM.InstalledPrinter("iDPRT SP410"); // 4x6 Label Thermal Printer

      const my_file = new JSPM.PrintFile(
        `${HOST_URL}/print/getLabel/${fileName}`,
        JSPM.FileSourceType.URL,
        `BoxContentLabel.png`,
        1
      );

      cpj.files.push(my_file);

      cpj.onFinished = () => {
        toast.dismiss(printingToast);
        toast.success("Finished printing!");
      };

      cpj.sendToClient();
    }
  };

  return { fileName };
};

export const printEbayAuctionLabel = async ({ sku }) => {
  const user = JSON.parse(localStorage.getItem("user"));
  const userId = user?.userID;

  const response = (
    await axios.post(`${HOST_URL}/print/ebayAuctionLabel`, {
      userId,
      sku,
    })
  ).data;

  let fileName;
  if (response.success) {
    fileName = response.fileName;
  } else {
    toast.error(response.message);
    return { message: response.message };
  }

  JSPM.JSPrintManager.license_url = `${HOST_URL}/jspm`;
  JSPM.JSPrintManager.auto_reconnect = true;
  JSPM.JSPrintManager.start();
  JSPM.JSPrintManager.WS.onStatusChanged = async function () {
    if (JSPM.JSPrintManager.websocket_status === JSPM.WSStatus.Open) {
      const cpj = new JSPM.ClientPrintJob();
      cpj.clientPrinter = new JSPM.DefaultPrinter();

      const my_file = new JSPM.PrintFile(
        `${HOST_URL}/print/getLabel/${fileName}`,
        JSPM.FileSourceType.URL,
        `EbayAuctionLabel.png`,
        1
      );

      cpj.files.push(my_file);

      cpj.sendToClient();
    }
  };

  return { fileName };
};

const services = {
  printLpnLabel,
  printListingLabel,
  printBinStoreLabels,

  printCartonLabel,
  printPalletLabel,
  printFbaShipmentLabel,
  printRackLabels,
  generateFbaShipmentLabels,
  printBoxContentLabel,
  printEbayAuctionLabel,
};

export default services;
